import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthProvider.tsx";
import PortalSidebar from "../components/PortalSidebar.tsx";
import { buildApiUrl } from "../services/apiConfig.ts";
import "./overview.css";

// ============================
// API types (server responses)
// ============================
interface ApiAd {
  id: string;
  start_date: string; // dd/mm/YYYY
  end_date: string;   // dd/mm/YYYY
  budget: number;
  budget_used?: number;
  impressions_inferred?: number;
  cpk: number;
  exclusive: boolean;
  title: string;
  description: string;
  submitted?: boolean;
  payment_confirmed?: boolean;
}

interface ApiMetrics {
  status: "success";
  metrics: {
    total_pageviews: number; // -> QR code scans (events)
    total_uniques: number;   // -> unique visitors (reach)
    car_ads_ids: string[];
    total_impressions_inferred?: number;
    ad_start?: string | null;
    ad_end?: string | null;
    timeseries: Array<{ date: string; pageviews: number; visitors: number; impressions_inferred?: number; budget_used?: number; active_cars?: number }>;
  };
}

// ============================
// View model
// ============================
export type Campaign = {
  id: string;
  name: string;
  status: "active" | "scheduled" | "ended" | "draft";
  startDate: string;
  endDate: string;
  budget: number;
  spend: number;
  impressions: number;
  reach: number;
  drivers: number;
  clicks: number;
  conversions: number;
  submitted?: boolean;
  paymentConfirmed?: boolean;
};

// ============================
// Utilities
// ============================
const fmtCurrency = (n:number) => `${new Intl.NumberFormat('en-US',{maximumFractionDigits:0}).format(n)} CHF`;
const fmtInt = (n:number) => new Intl.NumberFormat(undefined,{maximumFractionDigits:0}).format(n);
const pct = (num:number,den:number)=> den===0?0:(num/den)*100;
const toLocaleDate = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  // Swiss format with dots: DD.MM.YYYY
  return `${dd}.${mm}.${yyyy}`;
};

const ddmmyyyyToISO = (s: string) => {
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return s;
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}`;
};

export type SortKey = keyof Campaign | "cpc" | "cpm" | "spentPct";
const metric = (c:Campaign,k:SortKey)=>{
  if(k==="cpc") return c.clicks? c.spend/c.clicks : Infinity;
  if(k==="cpm") return c.impressions? (c.spend/c.impressions)*1000 : Infinity;
  if(k==="spentPct") return pct(c.spend,c.budget);
  return (c as any)[k];
};

const inferCampaignStatus = (ad: ApiAd, start: Date, end: Date, now: Date): Campaign["status"] => {
  const paymentConfirmed = ad.payment_confirmed === true;
  const awaitingPayment = ad.submitted === true && !paymentConfirmed;

  if (awaitingPayment) return "scheduled";
  if (end < now) return "ended";
  if (start > now) return ad.submitted === true ? "scheduled" : "draft";
  return ad.submitted === false ? "draft" : "active";
};

function CampaignCard({
  campaign,
  onView,
  onEdit,
  onDelete,
  onFinalize,
  onShowPayment,
  isOrgAdmin,
}: {
  campaign: Campaign;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onFinalize: () => void;
  onShowPayment: () => void;
  isOrgAdmin: boolean;
}) {
  const cpm = metric(campaign, "cpm");
  const awaitingPayment = campaign.submitted === true && campaign.paymentConfirmed !== true;
  const statusClass = campaign.status === 'active' ? 'active' :
                      campaign.status === 'scheduled' ? 'scheduled' :
                      campaign.status === 'draft' ? 'draft' : 'ended';
  const statusLabel = campaign.status === 'active' ? 'Aktiv' :
                      campaign.status === 'scheduled' ? (awaitingPayment ? 'Zahlung offen' : 'Eingereicht') :
                      campaign.status === 'draft' ? 'Entwurf' : 'Beendet';

  return (
    <article className="portal-panel portal-mobile-panel">
      <div className="portal-mobile-panel-top">
        <div>
          <div className="portal-mobile-title">{campaign.name}</div>
          <div className="portal-mobile-subtitle">
            {toLocaleDate(campaign.startDate)} – {toLocaleDate(campaign.endDate)}
          </div>
        </div>
        <span className={`portal-pill ${statusClass}`}>{statusLabel}</span>
      </div>

      <div className="portal-mobile-grid">
        <div className="portal-mobile-field">
          <span className="portal-mobile-label">Fahrer</span>
          <span>{fmtInt(campaign.drivers)}</span>
        </div>
        <div className="portal-mobile-field">
          <span className="portal-mobile-label">Impressionen</span>
          <span>{fmtInt(campaign.impressions)}</span>
        </div>
        <div className="portal-mobile-field">
          <span className="portal-mobile-label">QR Code Scans</span>
          <span>{fmtInt(campaign.clicks)}</span>
        </div>
      </div>

      <div className="portal-mobile-grid portal-mobile-grid-compact">
        <div className="portal-mobile-field">
          <span className="portal-mobile-label">Budget</span>
          <span>{fmtCurrency(campaign.budget)}</span>
        </div>
        <div className="portal-mobile-field">
          <span className="portal-mobile-label">Budget verbraucht</span>
          <span>{campaign.spend ? fmtCurrency(campaign.spend) : '-'}</span>
        </div>
        <div className="portal-mobile-field">
          <span className="portal-mobile-label">TKP</span>
          <span>{cpm === Infinity ? '-' : `${Math.round(cpm)} CHF`}</span>
        </div>
      </div>

      <div className="portal-mobile-actions">
        {campaign.status === 'draft' && (
          <>
            {isOrgAdmin ? <button className="btn ghost" onClick={onFinalize}>Starten</button> : <span className="btn disabled">Warten auf Admin</span>}
            <button className="btn light" onClick={onEdit}>Editieren</button>
            <button className="btn warn" onClick={onDelete}>Löschen</button>
          </>
        )}
        {campaign.status === 'scheduled' && (
          awaitingPayment ? (
            <button className="btn warn" onClick={onShowPayment}>Zahlungsinfo</button>
          ) : (
            <button className="btn disabled" disabled>In Prüfung</button>
          )
        )}
        {(campaign.status === 'active' || campaign.status === 'ended') && (
          <button className={`btn ${campaign.status === 'active' ? 'dark' : 'light'}`} onClick={onView}>Detail</button>
        )}
      </div>
    </article>
  );
}

// ============================
// Component
// ============================
export default function CampaignsOverview(){
  const navigate = useNavigate();
  const { fetchWithAuth, isOrgAdmin } = useAuth();

  const [query,setQuery]=useState("");
  const [status,setStatus]=useState<string>("all");
  const [sortKey,setSortKey]=useState<SortKey>("startDate");
  const [sortDir,setSortDir]=useState<"asc"|"desc">("desc");
  const [navOpen, setNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string| null>(null);
  const [data, setData] = useState<Campaign[]>([]);
  const [paymentDialog, setPaymentDialog] = useState<{ adId: string } | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 1200px)');
    const handle = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    setIsMobile(mq.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', handle);
    } else {
      mq.addListener(handle);
    }
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener('change', handle);
      } else {
        mq.removeListener(handle);
      }
    };
  }, []);

  // Fetch ads + metrics
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetchWithAuth(buildApiUrl("/ad"), { method: "GET" });
        if (!res.ok) throw new Error(`GET /ad failed: ${res.status}`);
        const json = await res.json();
        const ads: ApiAd[] = json.ads || [];

        const now = new Date();
        const enriched: Campaign[] = await Promise.all(
          ads.map(async (a) => {
            let spend = Number(a?.budget_used ?? 0);
            let impressions = Number(a?.impressions_inferred ?? 0);
            let reach = 0;
            let drivers = 0;
            let qrScans = 0;
            try {
              const mRes = await fetchWithAuth(buildApiUrl(`/ad/metrics?ad_id=${encodeURIComponent(a.id)}`));
              if (mRes.ok) {
                const m: ApiMetrics = await mRes.json();
                // Prefer inferred impressions if provided by metrics, else keep ad-level
                impressions = m?.metrics?.total_impressions_inferred ?? impressions;
                reach = m?.metrics?.total_uniques ?? 0;
                drivers = Array.isArray(m?.metrics?.car_ads_ids) ? m.metrics.car_ads_ids.length : 0;
                // Sum QR scans from timeseries if available, else use aggregate total
                const ts = m?.metrics?.timeseries || [];
                if (Array.isArray(ts) && ts.length) {
                  // Match the detail panel, which calculates effective TKP from
                  // cumulative timeseries spend and impressions.
                  spend = ts.reduce((sum, d) => sum + Number(d?.budget_used ?? 0), 0);
                  impressions = ts.reduce((sum, d) => sum + Number(d?.impressions_inferred ?? 0), 0);
                  qrScans = ts.reduce((sum, d) => sum + Number(d?.pageviews ?? 0), 0);
                } else {
                  qrScans = m?.metrics?.total_pageviews ?? 0;
                }
              }
            } catch {}

            const isoStart = ddmmyyyyToISO(a.start_date);
            const isoEnd = ddmmyyyyToISO(a.end_date);
            const start = new Date(isoStart);
            const end = new Date(isoEnd);
            const inferred = inferCampaignStatus(a, start, end, now);

            return {
              id: a.id,
              name: a.title,
              status: inferred,
              startDate: isoStart,
              endDate: isoEnd,
              budget: a.budget,
              spend,
              impressions,
              reach,
              drivers,
              // Use QR code scans from Plausible events
              clicks: qrScans,
              conversions: 0,
              submitted: a.submitted,
              paymentConfirmed: a.payment_confirmed === true,
            };
          })
        );

        if (!cancelled) setData(enriched);
      } catch (e:any) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchWithAuth]);

  const filtered = useMemo(()=> data
    .filter(c=> status==="all" ? true : c.status===status)
    .filter(c=>{
      const q=query.toLowerCase();
      return c.name.toLowerCase().includes(q)
        || c.id.toLowerCase().includes(q);
    }),[data,query,status]);

  const sorted = useMemo(()=>{
    const arr=[...filtered];
    arr.sort((a,b)=>{
      const va=metric(a,sortKey), vb=metric(b,sortKey);
      if(va<vb) return sortDir==="asc"?-1:1;
      if(va>vb) return sortDir==="asc"?1:-1;
      return 0;
    });
    return arr;
  },[filtered,sortKey,sortDir]);

  const finalizeAd = async (adId: string) => {
    if (!window.confirm("Are you sure you want to finalize/submit this campaign?")) return;
    try {
      const res = await fetchWithAuth(buildApiUrl(`/ad/submit?ad_id=${encodeURIComponent(adId)}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`Failed to submit: ${t}`);
      }
      await refetchCampaigns();
      setPaymentDialog({ adId });
    } catch (e: any) {
      alert(e.message || "Failed to finalize campaign.");
    }
  };

  const deleteAd = async (adId: string) => {
    if (!window.confirm("Entwurf wirklich löschen? Dies kann nicht rückgängig gemacht werden.")) return;
    try {
      const res = await fetchWithAuth(buildApiUrl(`/ad?ad_id=${encodeURIComponent(adId)}`), {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`Löschen fehlgeschlagen: ${t}`);
      }
      await refetchCampaigns();
    } catch (e: any) {
      alert(e.message || "Löschen fehlgeschlagen.");
    }
  };

  const refetchCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(buildApiUrl("/ad"), { method: "GET" });
      if (!res.ok) throw new Error(`GET /ad failed: ${res.status}`);
      const json = await res.json();
      const ads: ApiAd[] = json.ads || [];

      const now = new Date();
      const enriched: Campaign[] = await Promise.all(
        ads.map(async (a) => {
          let spend = Number(a?.budget_used ?? 0);
          let impressions = Number(a?.impressions_inferred ?? 0);
          let reach = 0;
          let drivers = 0;
          let qrScans = 0;
          try {
            const mRes = await fetchWithAuth(buildApiUrl(`/ad/metrics?ad_id=${encodeURIComponent(a.id)}`));
            if (mRes.ok) {
              const m: ApiMetrics = await mRes.json();
              impressions = m?.metrics?.total_impressions_inferred ?? impressions;
              reach = m?.metrics?.total_uniques ?? 0;
              drivers = Array.isArray(m?.metrics?.car_ads_ids) ? m.metrics.car_ads_ids.length : 0;
              // Sum QR scans from timeseries if available, else use aggregate total
              const ts = m?.metrics?.timeseries || [];
              if (Array.isArray(ts) && ts.length) {
                // Keep overview TKP consistent with the detail panel.
                spend = ts.reduce((sum, d) => sum + Number(d?.budget_used ?? 0), 0);
                impressions = ts.reduce((sum, d) => sum + Number(d?.impressions_inferred ?? 0), 0);
                qrScans = ts.reduce((sum, d) => sum + Number(d?.pageviews ?? 0), 0);
              } else {
                qrScans = m?.metrics?.total_pageviews ?? 0;
              }
            }
          } catch {}

          const isoStart = ddmmyyyyToISO(a.start_date);
          const isoEnd = ddmmyyyyToISO(a.end_date);
          const start = new Date(isoStart);
          const end = new Date(isoEnd);
          const inferred = inferCampaignStatus(a, start, end, now);

          return {
            id: a.id,
            name: a.title,
            status: inferred,
            startDate: isoStart,
            endDate: isoEnd,
            budget: a.budget,
            spend,
            impressions,
            reach,
            drivers,
            // Use QR code scans from Plausible events
            clicks: qrScans,
            conversions: 0,
            submitted: a.submitted,
            paymentConfirmed: a.payment_confirmed === true,
          };
        })
      );
      setData(enriched);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const openPaymentDialog = useCallback((adId: string) => {
    setPaymentDialog({ adId });
  }, []);

  const closePaymentDialog = () => {
    setPaymentDialog(null);
  };

  const sidebarClass = isMobile ? `portal-sidebar-drawer${navOpen ? " open" : ""}` : "";

  return (
    <div className="portal-root">
      <PortalSidebar
        className={sidebarClass}
        mobile={isMobile}
        onClose={() => setNavOpen(false)}
      />
      {navOpen && isMobile && (
        <button
          className="portal-drawer-backdrop"
          onClick={() => setNavOpen(false)}
          aria-label="Navigation schließen"
        />
      )}
      <main className="portal-main">
        <div className="portal-topbar portal-topbar-wide">
          <div className="portal-topbar-left">
            {isMobile && (
              <button
                className="portal-mobile-menu"
                onClick={() => setNavOpen(true)}
                aria-label="Navigation öffnen"
              >
                ☰
              </button>
            )}
            <div className="portal-title">Kampagnen</div>
          </div>
          <button className="portal-create" title="Neu" onClick={()=>navigate("/campaigns/new")}>+</button>
        </div>
        <div className="portal-search">
          <input
            aria-label="Suchen nach Name oder ID"
            placeholder="Suchen nach Name oder ID"
            value={query}
            onChange={(e)=>setQuery(e.target.value)}
          />
          <select
            className="portal-filter"
            aria-label="Kampagnenstatus filtern"
            value={status}
            onChange={(e)=>setStatus(e.target.value)}
          >
            <option value="all">Alle</option>
            <option value="active">Aktiv</option>
            <option value="scheduled">Eingereicht</option>
            <option value="draft">Entwurf</option>
            <option value="ended">Beendet</option>
          </select>
        </div>

        {loading && <div>Loading…</div>}
        {error && <div style={{background:'#c14725',padding:12,borderRadius:12}}>{error}</div>}

        {!loading && !error && (
          isMobile ? (
            <section className="portal-panels">
              {sorted.map((c) => (
                <CampaignCard
                  key={c.id}
                  campaign={c}
                  onView={() => navigate(`/campaigns/${c.id}`)}
                  onEdit={() => navigate(`/campaigns/edit/${c.id}`)}
                  onDelete={() => deleteAd(c.id)}
                  onFinalize={() => finalizeAd(c.id)}
                  isOrgAdmin={isOrgAdmin}
                  onShowPayment={() => openPaymentDialog(c.id)}
                />
              ))}
              {!sorted.length && (
                <div className="portal-empty-panel">Keine Kampagnen gefunden</div>
              )}
            </section>
          ) : (
            <section className="portal-table">
              <table className="portal-table2">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Start Datum</th>
                    <th>End Datum</th>
                    <th>Status</th>
                    <th className="portal-right">Fahrer</th>
                    <th className="portal-right">Impressionen</th>
                    <th className="portal-right">QR Code Scans</th>
                    <th className="portal-right">Budget</th>
                    <th className="portal-right">Budget verbraucht</th>
                    <th className="portal-right">TKP</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                {sorted.map(c=>{
                  const cpm = metric(c,"cpm");
                  const awaitingPayment = c.submitted === true && c.paymentConfirmed !== true;
                  const statusClass = c.status === 'active' ? 'active' :
                                      c.status === 'scheduled' ? 'scheduled' :
                                      c.status === 'draft' ? 'draft' : 'ended';
                  const statusLabel = c.status === 'active' ? 'Aktiv' :
                                      c.status === 'scheduled' ? (awaitingPayment ? 'Zahlung offen' : 'Eingereicht') :
                                      c.status === 'draft' ? 'Entwurf' : 'Beendet';
                  const drivers = c.drivers ?? 0;
                  return (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td>{toLocaleDate(c.startDate)}</td>
                      <td>{toLocaleDate(c.endDate)}</td>
                      <td><span className={`portal-pill ${statusClass}`}>{statusLabel}</span></td>
                      <td className="portal-right">{fmtInt(drivers)}</td>
                      <td className="portal-right">{fmtInt(c.impressions)}</td>
                      <td className="portal-right">{fmtInt(c.clicks)}</td>
                      <td className="portal-right">{fmtCurrency(c.budget)}</td>
                      <td className="portal-right">{c.spend ? fmtCurrency(c.spend) : <span className="portal-cell-muted">-</span>}</td>
                      <td className="portal-right">{cpm===Infinity?"-":`${Math.round(cpm)} CHF`}</td>
                      <td>
                        <div className="portal-actions">
                          {c.status === 'draft' && (
                            <>
                              {isOrgAdmin ? <button className="btn ghost" onClick={()=>finalizeAd(c.id)}>Starten</button> : <span className="btn disabled">Warten auf Admin</span>}
                              <button className="btn light" onClick={()=>navigate(`/campaigns/edit/${c.id}`)}>Editieren</button>
                              <button className="btn warn" onClick={()=>deleteAd(c.id)}>Löschen</button>
                            </>
                          )}
                          {c.status === 'scheduled' && (
                            awaitingPayment ? (
                              <button
                                className="btn warn"
                                onClick={()=>openPaymentDialog(c.id)}
                              >
                                Zahlungsinfo
                              </button>
                            ) : (
                              <button className="btn disabled" disabled>In Prüfung</button>
                            )
                          )}
                          {c.status === 'active' && (
                            <button className="btn dark" onClick={()=>navigate(`/campaigns/${c.id}`)}>Detail</button>
                          )}
                          {c.status === 'ended' && (
                            <button className="btn light" onClick={()=>navigate(`/campaigns/${c.id}`)}>Detail</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!sorted.length && (
                  <tr><td colSpan={11} style={{textAlign:'center',background:'transparent',color:'#d3e5dd'}}>Keine Kampagnen gefunden</td></tr>
                )}
              </tbody>
            </table>
          </section>
        ))}
        {paymentDialog && (
          <div className="portal-modal-backdrop" onClick={closePaymentDialog}>
            <div className="portal-modal" onClick={(e) => e.stopPropagation()}>
              <div className="portal-modal-top">
                <div>
                  <div className="portal-modal-eyebrow">Kampagne {paymentDialog.adId.slice(0, 8)}</div>
                  <div className="portal-modal-title">Zahlungsinformationen</div>
                </div>
                <button className="portal-modal-close" onClick={closePaymentDialog} aria-label="Dialog schließen">
                  X
                </button>
              </div>
              <p className="portal-modal-body">
                Die Rechnung wird Ihnen in Kürze per E-Mail zugesendet.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
